import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import flow from 'lodash/flow';
import { Set, Map, fromJS } from 'immutable';
import { token } from '@atlaskit/tokens';
import {
	StaticSearch as StaticSearchDI,
	SearchParamsUpdate as SearchParamsUpdateDI,
	FacetType as FacetTypeDI,
} from '@atlassian/amkt-addon-discovery';
import {
	InstallationLockProvider,
	AppInstallAnalyticsListener,
} from '@atlassian/emcee/app-install';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useMarketPlaceDialog } from '@atlassian/jira-marketplace-dialog/src/services/index.tsx';
import { getEmceeAppListingLink } from '@atlassian/jira-platform-marketplace-url-resolver';
import type { Props } from '../common/types';

// This data is okay to be checked into git and bundled to webpack
// We're following the same pattern as https://bitbucket.org/atlassian-marketplace/marketplace-frontend/src/master/packages/amkt-addon-discovery/dev/algolia_config.json
const algoliaConfig = {
	applicationId: '7LVR1JKTDF',
	apiKey: '3ef48f0fc67ee7e9c5a9dbbf6b45908c',
} as const;

const landingPagesFilters = Map({
	product: 'jira',
	hosting: 'cloud',
});

const buildAppUrl = (
	addonKey: string,
	addonId: string,
	isJiraAdmin: boolean,
	source: string,
	projectKey?: string | null,
	projectId?: number | null,
	installDialogOpen?: boolean,
	requestDialogOpen?: boolean,
): string => {
	if (isJiraAdmin) {
		return getEmceeAppListingLink(
			addonKey,
			{
				source,
				installDialogOpen,
			},
			{},
			'DISCOVER',
		);
	}
	if (ff('enduser-emcee-discover') && projectKey !== null && projectId !== null) {
		return getEmceeAppListingLink(
			addonKey,
			{
				source,
				requestDialogOpen,
			},
			{
				'project.key': projectKey,
				'project.id': String(projectId),
			},
		);
	}
	// Project admins without jira admin will be redirected to MPAC instead, otherwise they'll see an 'Access denied' page
	return `https://marketplace.atlassian.com/apps/${addonId}`;
};

const MarketplaceAppTiles = (props: Props) => {
	const {
		// @ts-expect-error - TS2339 - Property 'StaticSearch' does not exist on type 'Props'.
		StaticSearch = StaticSearchDI,
		// @ts-expect-error - TS2339 - Property 'SearchParamsUpdate' does not exist on type 'Props'.
		SearchParamsUpdate = SearchParamsUpdateDI,
		// @ts-expect-error - TS2339 - Property 'FacetType' does not exist on type 'Props'.
		FacetType = FacetTypeDI,
		categoryTitle,
		marketingLabel,
		onSortHits,
		colSpan,
		extraApp: ExtraApp,
		isJiraAdmin,
		source,
		projectKey,
		projectId,
		installDialogOpen,
		requestDialogOpen,
		actionSubjectId,
		installInContext,
		onTileClicked,
		onAppInstallSuccess,
		onAppInstallStarted,
		onAppInstallFailure,
		onDismissSuccessModal,
	} = props;

	const [shouldRefetch, setShouldRefetch] = useState(false);

	useEffect(() => {
		// We aren't passing the `onAppInstallSuccess` callback to children so we're instead relying on
		// children updating the `shouldRefetch` state to trigger the callback. This fixes a race condition where
		// refetching would occur before modals closed, resulting in a clunky UX. Suspect this is a symptom of
		// React.Suspense not unmounting child content rendered in portals (modals) while suspended.
		if (shouldRefetch) {
			onAppInstallSuccess?.();
			setShouldRefetch(false);
		}
	}, [shouldRefetch, onAppInstallSuccess]);

	const { marketplaceDialog, openMarketplaceDialog } = useMarketPlaceDialog({
		requestLocation: source,
		...(fg('jira_improve_scm_onboarding_gate') && {
			onDismissSuccessModal,
		}),
	});

	const hitContext = Map({
		// @ts-expect-error - TS7031 - Binding element 'hit' implicitly has an 'any' type.
		hitHrefResolver: ({ hit }) => {
			const addonKey = hit.get('addonKey');
			const addonId = hit.get('addonId');
			return buildAppUrl(
				addonKey,
				addonId,
				isJiraAdmin,
				source,
				projectKey,
				projectId,
				installDialogOpen,
				requestDialogOpen,
			);
		},
		// @ts-expect-error - TS7031 - Binding element 'hit' implicitly has an 'any' type.
		hitImageHrefResolver: ({ hit }) => {
			const logoImagePath = hit.getIn(['logo', 'highRes']) || hit.getIn(['logo', 'image']);
			return `https://marketplace-cdn.atlassian.com/files/${logoImagePath}`;
		},
		// onHitClick callback is required, but this also causes the anchor link to do nothing when clicked,
		// hence the window.location.href workaround here
		// @ts-expect-error - TS7031 - Binding element 'hit' implicitly has an 'any' type. | TS7031 - Binding element 'hitRank' implicitly has an 'any' type.
		onHitClick: ({ hit, hitRank }) => {
			const addonKey = hit.get('addonKey');
			const addonId = hit.get('addonId');

			onTileClicked &&
				onTileClicked(
					addonKey,
					hitRank,
					isJiraAdmin,
					actionSubjectId,
					// @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'number | null'.
					projectId,
					projectKey,
				);

			// We still need installInContext as we don't want in context installation for these 2 places yet:
			// * Dev details dialog - we don't have a 'onSuccess' flow here yet
			// * JSM -> Project settings -> Apps page - Fusion doesn't own this. So we can't set it to true if they don't want it or not ready for it yet.
			if (installInContext === true) {
				openMarketplaceDialog({
					appKey: addonKey,
					appName: hit.get('name'),
					isAppFree: !(hit.get('version')?.get('paid') ?? true),
					onInstallSuccess: onAppInstallSuccess,
					onInstallStarted: onAppInstallStarted,
					onFailure: onAppInstallFailure,
				});
				return;
			}

			window.open(
				buildAppUrl(
					addonKey,
					addonId,
					isJiraAdmin,
					source,
					projectKey,
					projectId,
					installDialogOpen,
					requestDialogOpen,
				),
				'_blank',
			);
		},
	});

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766 */}
			<style>{'.app-title-font-workaround h3 { font-family: inherit; }'}</style>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<CategoryDiv className="app-title-font-workaround">
				{categoryTitle !== null ? <CategoryTitleH3>{categoryTitle}</CategoryTitleH3> : <></>}
				<StaticSearchWrapperDiv>
					{/* Show an extra app if available */}
					{ExtraApp}
					{/* See doc: node_modules/@atlassian/amkt-addon-discovery/es6/StaticSearch/App.js.flow */}
					<StaticSearch
						algoliaConfig={fromJS(algoliaConfig)}
						applyOverrides={
							new SearchParamsUpdate({
								types: Set([FacetType.disjunctive('marketingLabels')]),
								// @ts-expect-error - TS7006 - Parameter 'params' implicitly has an 'any' type.
								apply: (params) =>
									params.addDisjunctiveFacetRefinement('marketingLabels', marketingLabel),
							})
						}
						debug={false}
						filters={landingPagesFilters}
						environment="prod" // dev, stg, prod filters={landingPagesFilters}
						hitContext={hitContext}
						hitSize="large"
						screenClass="xl" // not yet using react-grid-system (should it?)
						sortHits={onSortHits}
						colSpan={colSpan}
					/>
					{marketplaceDialog}
				</StaticSearchWrapperDiv>
			</CategoryDiv>
		</>
	);
};

const MarketplaceAppTilesWrapped = (props: Props) => (
	<JSErrorBoundary
		id="marketplaceAppTiles"
		packageName="jiraMarketplaceAppTiles"
		sendToPrivacyUnsafeSplunk
		teamName="fusion-isotopes"
	>
		<InstallationLockProvider>
			<AppInstallAnalyticsListener>
				<MarketplaceAppTiles {...props} />
			</AppInstallAnalyticsListener>
		</InstallationLockProvider>
	</JSErrorBoundary>
);

export default flow(
	ComponentWithAnalytics('tile', {
		onTileClicked: 'clicked',
	}),
	withFireUiAnalytics({
		onTileClicked: (
			// @ts-expect-error - TS7006 - Parameter 'addonKey' implicitly has an 'any' type.
			addonKey,
			// @ts-expect-error - TS7006 - Parameter 'hitRank' implicitly has an 'any' type.
			hitRank,
			// @ts-expect-error - TS7006 - Parameter 'isJiraAdmin' implicitly has an 'any' type.
			isJiraAdmin,
			// @ts-expect-error - TS7006 - Parameter 'actionSubjectId' implicitly has an 'any' type.
			actionSubjectId,
			// @ts-expect-error - TS7006 - Parameter 'projectId' implicitly has an 'any' type.
			projectId,
			// @ts-expect-error - TS7006 - Parameter 'projectKey' implicitly has an 'any' type.
			projectKey,
		) => ({
			name: actionSubjectId,
			addonKey,
			hitRank,
			isJiraAdmin,
			projectId,
			projectKey,
		}),
	}),
)(MarketplaceAppTilesWrapped);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CategoryDiv = styled.div({
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CategoryTitleH3 = styled.h3({
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StaticSearchWrapperDiv = styled.div({
	marginLeft: token('space.negative.100', '-8px'),
	display: 'flex',
});
